/// <amd-module name="Wit/WorkItems/DataSources/WorkItemDataSource" />
import { endsWith, equals } from "VSS/Core/Util/String";
import { IVssPageContext, TeamFoundationHostType } from "VSS/Platform/Context";
import { JsonPatchDocument, JsonPatchOperation, Operation } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssLocationService } from "VSS/Platform/Location";
import { FieldStatusFlags, WorkItemLinkedItem } from "Wit/Clients/Internal/Contracts/SocialWorkItem";
import { WorkItem, WorkItemDelete, WorkItemErrorPolicy, WorkItemExpand, WorkItemRelation } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { CoreFieldRefNames } from "Wit/Common/CoreFieldRefNames";
import { ArtifactUpdateType, IFieldUpdate, IWorkItemLinkUpdate } from "Wit/WorkItems/WorkItemContracts";
import { FieldValueMap, FieldValueType } from "Wit/WorkItemTypes/Contracts";
import { getFieldFlags } from "./FieldFlagsDataSource";

interface ILinkUpdatePatch {
    updateType: ArtifactUpdateType;
    id: number;
    url: string;
    referenceName: string;
    comment?: string;
}

export interface IWorkItemUpdateResult {
    workItem: WorkItem;
    fieldFlags: { [fieldRefName: string]: FieldStatusFlags } | undefined;
}

export interface IFieldUpdateRequest extends IFieldUpdate {
    /**
     * Original field value, this is used to compare the state on the server to the client's view of the world to prevent conflicting
     * edits
     */
    originalValue?: FieldValueType;
}

export async function getWorkItemsAsync(pageContext: IVssPageContext, ids: number[], fields?: string[], projectGuid?: string): Promise<WorkItem[]> {
    const witClient = await getWitRestClient(pageContext);
    return witClient.getWorkItems(ids, projectGuid, fields, undefined, undefined, WorkItemErrorPolicy.Omit);
}

export async function updateWorkItemAsync(
    pageContext: IVssPageContext,
    workItemId: number,
    projectGuid: string,
    fieldValueMap?: { [fieldRefName: string]: IFieldUpdateRequest },
    linkChanges?: IWorkItemLinkUpdate[]
): Promise<IWorkItemUpdateResult> {
    const throwError = () => {
        throw new Error("No update found to process");
    };

    if (!fieldValueMap && !linkChanges) {
        throwError();
    }

    const linkUpdates = linkChanges && linkChanges.length > 0 ? { workItemId: workItemId, linkChanges } : undefined;
    const updateDocument = await _getUpdateDocument(pageContext, fieldValueMap, linkUpdates, true);

    if (updateDocument.length === 0) {
        throwError();
    }

    const witClient = await getWitRestClient(pageContext);
    const updatedModel: WorkItem = await witClient.updateWorkItem(
        updateDocument,
        workItemId,
        projectGuid,
        undefined,
        undefined,
        undefined,
        WorkItemExpand.All
    );

    // Make another call to get the field flags. Ideally this would be done in a single call, we will need to update the REST API to enable this.
    const fieldFlags = await getFieldFlags(pageContext, projectGuid, workItemId);

    // The value of history field would be the last comment entered.
    // Set it as empty during work item load to avoid unnecessary issues.
    updatedModel.fields[CoreFieldRefNames.History] = "";

    return {
        workItem: updatedModel,
        fieldFlags
    };
}

async function getWitRestClient(pageContext: IVssPageContext): Promise<IWorkItemTrackingRestClient> {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    await contributionService.getContributionAsync("ms.vss-work-web.client-wit");
    return pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
}

async function _getUpdateDocument(
    pageContext: IVssPageContext,
    fieldValueMap?: { [fieldRefName: string]: IFieldUpdateRequest },
    linkUpdate?: { workItemId?: number; linkChanges: IWorkItemLinkUpdate[] },
    testFieldRev?: boolean
): Promise<JsonPatchDocument & JsonPatchOperation[]> {
    const patchDocument: JsonPatchDocument & JsonPatchOperation[] = [];

    // find out if the work item update has any link update or delete op.
    const hasLinkUpdates = linkUpdate && linkUpdate.linkChanges && linkUpdate.linkChanges.some(l => l.updateType !== ArtifactUpdateType.Added);

    if (fieldValueMap) {
        for (const dirtyField in fieldValueMap) {
            const { originalValue, value } = fieldValueMap[dirtyField];

            // add field test operation only in edit mode (not during work item creation)
            // and only when there are no link updates or deletes in the work item update
            // because in that case we just add a test operation on revision
            if (testFieldRev && !hasLinkUpdates && !equals(dirtyField, CoreFieldRefNames.History, true)) {
                patchDocument.push({
                    op: Operation.Test,
                    path: `/fields/${dirtyField}`,
                    value: originalValue
                } as JsonPatchOperation);
            }

            patchDocument.push({
                op: Operation.Add,
                path: `/fields/${dirtyField}`,
                value: value !== undefined ? value : ""
            } as JsonPatchOperation);
        }
    }

    if (linkUpdate) {
        //
        // Process changes to work item links
        //
        const { workItemId, linkChanges } = linkUpdate;
        const cachedLinksResponses: { [workItemId: number]: { rev: number; relations: WorkItemRelation[] } } = {};

        for (const linkChange of linkChanges) {
            // We need to populate all links for this work item before making an edit.
            // This could be optimized with a change to the REST API.
            // Currently we need the index of the link in the list of all links to make edits, hence getting all links.
            const targetUrl = await getWorkItemUrl(pageContext, linkChange.targetWorkItemId, linkChange.targetProjectId);

            if (linkChange.updateType === ArtifactUpdateType.Added) {
                patchDocument.push({
                    op: Operation.Add,
                    path: "/relations/-",
                    value: { rel: linkChange.referenceName, url: targetUrl, attributes: { comment: linkChange.comment } }
                } as JsonPatchOperation);
            } else if (workItemId) {
                const getLinksResponse: { rev: number; relations: WorkItemRelation[] } = cachedLinksResponses.hasOwnProperty(workItemId)
                    ? cachedLinksResponses[workItemId]
                    : await getWorkItemLinksAsync(pageContext, workItemId);

                // Cache the response
                cachedLinksResponses[workItemId] = getLinksResponse;
                const index = getLinksResponse.relations.findIndex(
                    link => endsWith(link.url, `/${linkChange.targetWorkItemId}`) && equals(link.rel, linkChange.referenceName, true)
                );

                if (index === -1) {
                    continue;
                }

                // Validate we are on the correct revision as we need to use the current index
                patchDocument.push({
                    op: Operation.Test,
                    path: "/rev",
                    value: getLinksResponse.rev
                } as JsonPatchOperation);

                switch (linkChange.updateType) {
                    case ArtifactUpdateType.Removed:
                        patchDocument.push({
                            op: Operation.Remove,
                            path: `/relations/${index}`
                        } as JsonPatchOperation);
                        break;

                    case ArtifactUpdateType.Replaced:
                        patchDocument.push({
                            op: Operation.Add,
                            path: "/relations/-",
                            value: { rel: linkChange.referenceName, url: targetUrl, attributes: { comment: linkChange.comment } }
                        } as JsonPatchOperation);
                        patchDocument.push({
                            op: Operation.Remove,
                            path: `/relations/${index}`
                        } as JsonPatchOperation);
                        break;

                    case ArtifactUpdateType.Updated:
                        patchDocument.push({
                            op: Operation.Replace,
                            path: `/relations/${index}/attributes/comment`,
                            value: linkChange.comment
                        } as JsonPatchOperation);
                        break;
                }
            }
        }
    }

    return patchDocument;
}

/**
 * Create a new workItem asynchronously
 * @param pageContext current page context
 * @param projectId project id the workItem belongs to
 * @param workItemType workItem type name
 * @param fieldValueMap workItem field values map
 * @param linkChanges initial workItem links
 */
export async function createWorkItemAsync(
    pageContext: IVssPageContext,
    projectId: string,
    workItemType: string,
    fieldValueMap: FieldValueMap,
    linkChanges?: WorkItemLinkedItem[]
): Promise<WorkItem> {
    const witClient = await getWitRestClient(pageContext);
    const fieldChanges: { [fieldRefName: string]: IFieldUpdate } = {};
    for (const fieldRefName in fieldValueMap) {
        fieldChanges[fieldRefName] = { fieldRefName, value: fieldValueMap[fieldRefName] };
    }

    const updateDocument = await _getUpdateDocument(
        pageContext,
        fieldChanges,
        linkChanges && {
            linkChanges: linkChanges.map(l => {
                return { ...l, updateType: ArtifactUpdateType.Added };
            })
        }
    );

    const updatedModel: WorkItem = await witClient.createWorkItem(
        updateDocument,
        projectId,
        workItemType,
        undefined,
        undefined,
        undefined,
        WorkItemExpand.All
    );

    // The value of history field would be the last comment entered.
    // Set it as empty during work item load to avoid unnecessary issues.
    updatedModel.fields[CoreFieldRefNames.History] = "";

    return updatedModel;
}

export async function restoreWorkItemAsync(pageContext: IVssPageContext, workItemId: number, projectId: string): Promise<WorkItemDelete> {
    const witClient = await getWitRestClient(pageContext);
    return witClient.restoreWorkItem({ isDeleted: false }, workItemId, projectId);
}

export async function deleteWorkItemAsync(pageContext: IVssPageContext, workItemId: number, projectId: string): Promise<WorkItemDelete> {
    const witClient = await getWitRestClient(pageContext);
    return witClient.deleteWorkItem(workItemId, projectId);
}

async function getWorkItemLinksAsync(pageContext: IVssPageContext, workItemId: number): Promise<{ rev: number; relations: WorkItemRelation[] }> {
    const witClient = await getWitRestClient(pageContext);
    const workItemModel: WorkItem = await witClient.getWorkItem(workItemId, undefined, undefined, undefined, WorkItemExpand.Relations);

    // Note: Relations are undefined if workItem doesn't have any relations. Return empty array in this case
    return { rev: workItemModel.rev, relations: workItemModel.relations || [] };
}

async function getWorkItemUrl(pageContext: IVssPageContext, workItemId: number, projectId: string): Promise<string> {
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    const rootUrl = await locationService.getServiceLocationAsync(undefined, TeamFoundationHostType.ProjectCollection);

    return `${rootUrl}${projectId}/_apis/wit/workItems/${workItemId}`;
}
